import { useTranslation } from 'react-i18next';
import { Footer as DimFooter, type FooterColumn, type FooterLink } from '@bn-apps/dim-ui-react';
import { BaneNorLogo } from '@bn-apps/design-system-gfx';
import { useAuth } from '@hooks/useAuth';
import routes from '@config/routes';
import { visibleNavigation } from '@config/navigation';

const socialLinks: FooterLink[] = [
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/banenor/', isExternal: true },
  { label: 'Facebook', href: 'https://www.facebook.com/BaneNORSF', isExternal: true },
  { label: 'X', href: 'https://x.com/BaneNORSF', isExternal: true },
];

/**
 * Footer with all pages in columns, one per menu section, so they can also be found
 * with the browser's find on page. Pages behind sign in are only listed when signed in.
 */
const Footer = (): JSX.Element => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const columns: FooterColumn[] = [
    ...(user?.isAuthenticated ? visibleNavigation(user.isProjectMember === true) : []).map((section) => ({
      title: t(section.labelKey),
      links: section.pages.map((page) => ({ label: t(page.labelKey), href: page.path })),
    })),
    {
      title: t('help.title'),
      links: [
        { label: t('common.helpCenter'), href: routes.help },
        { label: t('footer.contactSupport'), href: `mailto:${t('footer.supportEmail')}` },
        {
          label: 'Azure DevOps',
          href: 'https://dev.azure.com/bn-apps/Digital%20Infrastrukturmodell%20(DIM)/',
          isExternal: true,
        },
        { label: 'GitHub', href: 'https://github.com/orgs/bn-apps/projects/8', isExternal: true },
      ],
    },
  ];

  const legalLinks: FooterLink[] = [
    { label: t('footer.privacy'), href: 'https://www.banenor.no/personvernerklaring/', isExternal: true },
    {
      label: t('footer.accessibility'),
      href: 'https://oppslagsverk.banenor.no/innholdsstrategi-eksterne-nettsider/overordnet-innholdsstrategi/redaksjonelle-retningslinjer/',
      isExternal: true,
    },
  ];

  return (
    <DimFooter
      appName={t('common.appTitle')}
      homeHref={routes.home}
      logo={<BaneNorLogo color="white" />}
      slogan={t('footer.slogan')}
      columns={columns}
      copyright={t('footer.copyright', { year: new Date().getFullYear(), title: t('landing.hero.title') })}
      legalLinks={legalLinks}
      socialLinks={socialLinks}
      externalLabel={t('footer.opensInNewTab')}
      navigationLabel={t('footer.navigation')}
    />
  );
};

export default Footer;
